import React from 'react';
import {ButtonLoader} from '../button-loader';

export enum ButtonSize {
  LG = 'lg',
  MD = 'md',
  SM = 'sm',
}

export enum ButtonType {
  BUTTON = 'button',
  SUBMIT = 'submit',
}

export enum ButtonVariant {
  Outline = 'outline',
  Primary = 'primary',
  Secondary = 'secondary',
}

interface ButtonProps {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  loadingText?: string;
  onClick?: () => void;
  size?: ButtonSize;
  startIcon?: React.ReactNode;
  type?: ButtonType;
  variant?: ButtonVariant;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  className = '',
  disabled = false,
  fullWidth = false,
  loading = false,
  loadingText,
  onClick,
  size = ButtonSize.MD,
  startIcon,
  type = ButtonType.BUTTON,
  variant = ButtonVariant.Primary,
}) => {
  const baseClasses =
    'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';

  const sizeClasses = {
    [ButtonSize.SM]: 'px-3 py-1.5 text-sm',
    [ButtonSize.MD]: 'px-4 py-2 text-base',
    [ButtonSize.LG]: 'px-6 py-3 text-lg',
  };

  const variantClasses = {
    [ButtonVariant.Primary]:
      'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed',
    [ButtonVariant.Secondary]:
      'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 disabled:bg-gray-100 disabled:cursor-not-allowed',
    [ButtonVariant.Outline]:
      'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-gray-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
  };

  const widthClass = fullWidth ? 'w-full' : '';

  return (
    <button
      className={`${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${widthClass} ${className}`.trim()}
      disabled={disabled || loading}
      onClick={onClick}
      type={type}>
      {loading ? (
        <>
          <ButtonLoader />
          {loadingText != null && <span className="ml-2">{loadingText}</span>}
        </>
      ) : (
        <>
          {startIcon != null && <span className="mr-2">{startIcon}</span>}
          {children}
        </>
      )}
    </button>
  );
};
